import { getGetSpacePricesQueryKey } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import type { PricesEntry } from '@cocoonspace/sdk-js/types/pricesEntry'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useAuth } from '@cocoonspace/shared/domains/auth/hooks/use-auth.hook'
import { useAuthDialogStore } from '@cocoonspace/shared/domains/auth/stores/use-auth-dialog-store.hook'
import { useCreateMultidateBooking } from '@cocoonspace/shared/hooks/cocoon-api'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { toast } from 'sonner'
import { Price } from '~/components/price'
import { Button } from '~/components/ui/button'

const BookingOptionsCount = ({ count }: { count: number }) => (
	<Trans
		parent='div'
		className='flex flex-row gap-x-1'
		i18nKey='booking:multidate.priceFooter.selectedDays'
		values={{ count }}
		components={{ strong: <strong className='text-primary' /> }}
	/>
)

const DiscountedPrices = ({
	price_day,
	price_day_discounted,
}: { price_day?: number; price_day_discounted?: number }) => (
	<div className='flex flex-row items-center gap-1 text-sm'>
		<Trans i18nKey='common:thatIs' />

		<Price
			price={price_day}
			crossed
			className='font-normal'
		/>

		<Price
			price={price_day_discounted}
			className='text-primary'
		/>
	</div>
)

export const BookingSidebarMultidateFooter = ({
	space,
	bookingOptions,
	daysPrices,
	setBookingOptions,
}: {
	space: Space
	bookingOptions: Date[]
	setBookingOptions: (options: Date[]) => void
	daysPrices: Record<string, PricesEntry>
}) => {
	const router = useRouter()
	const { isAuth } = useAuth()
	const { t } = useTranslation()

	const queryClient = useQueryClient()

	const openDialog = useAuthDialogStore((state) => state.openDialog)

	const { mutateAsync: createBooking, isLoading } = useCreateMultidateBooking()

	const totalPrices = useMemo(() => {
		return bookingOptions.reduce<{
			price_day?: number
			price_day_discounted?: number
		}>(
			(acc, day) => {
				const formattedDay = dateTimeUtils(day).customFormat('dateTech')

				if (!daysPrices[formattedDay]) return acc

				const { price_day_discounted, price_day } = daysPrices[formattedDay]

				return {
					price_day:
						price_day !== undefined
							? (acc.price_day || 0) + price_day
							: undefined,
					price_day_discounted:
						price_day_discounted !== undefined
							? (acc.price_day_discounted || 0) + price_day_discounted
							: undefined,
				}
			},
			{ price_day: undefined, price_day_discounted: undefined },
		)
	}, [bookingOptions, daysPrices])

	const hasDiscount =
		totalPrices?.price_day !== undefined &&
		totalPrices?.price_day_discounted !== undefined &&
		totalPrices?.price_day !== totalPrices?.price_day_discounted

	const handleSubmit = async () => {
		if (space.status !== 'published') return

		if (!isAuth) {
			router.push(
				{
					query: {
						...router.query,
						day: bookingOptions.map((option) =>
							dateTimeUtils(option).customFormat('dateTech'),
						),
					},
				},
				undefined,
				{ shallow: true },
			)

			openDialog({
				activeTab: 'register',
				errorMsg: t('errors:mustBeAuthToBook'),
			})
		}

		return createBooking({ bookingOptions, space })
			.then((res) => {
				router.push({
					pathname: '/booking/[bookingId]/payment',
					query: { bookingId: res.booking.id },
				})
			})
			.catch((err) => {
				// * remove unavailable bookingOptions
				setBookingOptions(
					bookingOptions.filter((option) => {
						const dateTechOption =
							dateTimeUtils(option).customFormat('dateTech')

						return !err.data.some((e: PricesEntry) => e.day === dateTechOption)
					}),
				)

				// * invalidate calendar prices to reset unavailable days
				queryClient.invalidateQueries(getGetSpacePricesQueryKey(space.id))

				if (err.message === 'unavailable') {
					toast.error(
						<Trans i18nKey={'booking:multidate.errors.availability'} />,
					)
				} else {
					toast.error(t('errors:generic'))
				}
			})
	}

	return (
		<div className='flex min-h-[93px] flex-col justify-between border-slate-200 border-t p-4'>
			<BookingOptionsCount count={bookingOptions.length} />

			<div className='flex flex-row items-end'>
				<div className='flex flex-col'>
					{hasDiscount && <DiscountedPrices {...totalPrices} />}

					<Price
						price={
							totalPrices?.price_day_discounted !== undefined
								? totalPrices?.price_day_discounted
								: totalPrices?.price_day
						}
						taxType='ht'
						className='mt-auto flex flex-row text-2xl leading-tight'
						taxClassName='mb-[4px] text-xxs'
					/>
				</div>

				<Button
					data-testid='book-space'
					variant='gradient'
					disabled={!bookingOptions.length || isLoading}
					className='ml-auto'
					onClick={handleSubmit}
				>
					{t('actions:book.btnLabel')}
				</Button>
			</div>
		</div>
	)
}
